import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LOGO, api } from "../lib/api";
import { LayoutDashboard, Bot, ListChecks, BookOpen, Film, NotebookPen, Users, Newspaper, MessageSquare, UserCircle, Settings, LogOut, Target, Link2, Car, MessagesSquare, Compass, Map } from "lucide-react";

const ITEMS = [
  { to: "/app", label: "Panel", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"], end: true },
  { to: "/app/gestion", label: "Gestión", icon: Settings, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR"] },
  { to: "/app/recomendados", label: "Recomendados para Mí", icon: Target, roles: ["ALUMNO"] },
  { to: "/app/dakota", label: "Dakota IA", icon: Bot, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/tests", label: "Tests", icon: ListChecks, roles: ["ALUMNO", "PROFESOR", "SUPER_ADMIN"] },
  { to: "/app/examenes", label: "Exámenes Prácticos", icon: Car, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/competencias", label: "Mapa de Competencias", icon: Map, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/mensajes", label: "Mensajes", icon: MessagesSquare, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/recursos", label: "Biblioteca de Recursos", icon: BookOpen, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/multimedia", label: "Biblioteca Multimedia", icon: Film, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/academia/drive", label: "Natur Drive 360", icon: Compass, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"], premium: true },
  { to: "/app/enlaces", label: "Enlaces", icon: Link2, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/notas", label: "Libro de Notas", icon: NotebookPen, roles: ["ALUMNO", "PROFESOR"] },
  { to: "/app/comunidad", label: "Comunidad", icon: Users, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/blog", label: "Blog", icon: Newspaper, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/comunicacion", label: "Comunicación", icon: MessageSquare, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
  { to: "/app/perfil", label: "Mi Perfil", icon: UserCircle, roles: ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR", "ALUMNO"] },
];

const ROLE_LABEL = { SUPER_ADMIN: "Matriz", FRANQUICIA: "Franquicia", PROFESOR: "Profesor", ALUMNO: "Alumno" };

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const fetchUnread = () => api.get("/chat/unread-total").then((r) => setUnread(r.data.total || 0)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  useEffect(() => {
    const mod = location.pathname.replace("/app", "").replace("/", "") || "panel";
    api.post("/track", { module: mod }).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="w-72 shrink-0 bg-[#1B1D23] text-neutral-400 flex-col hidden md:flex sticky top-0 h-screen">
        <div className="px-6 pt-6 pb-5 border-b border-white/5">
          <div className="bg-white rounded-2xl px-4 py-3 flex items-center justify-center shadow-lg shadow-black/20">
            <img src={LOGO} alt="Natur Autoescuela" className="h-20 w-auto object-contain" />
          </div>
          <p className="mt-3 text-center text-[11px] tracking-[0.4em] uppercase text-orange-400 font-semibold font-heading">Natur Academy</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-5 px-4 space-y-1">
          {ITEMS.filter((i) => i.roles.includes(user.role)).map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                     className={({ isActive }) =>
                       `flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13.5px] transition-colors ${
                         isActive
                           ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white font-medium shadow-md shadow-orange-950/40"
                           : "hover:bg-white/5 hover:text-white"}`}>
              <i.icon size={17} strokeWidth={1.75} /> <span className="flex-1">{i.label}</span>
              {i.to === "/app/mensajes" && unread > 0 && (
                <span data-testid="unread-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
              {i.premium && <span className="text-[8px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-black">360</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-5 border-t border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-600/20 text-orange-400 flex items-center justify-center text-sm font-semibold font-heading shrink-0">
              {user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-[11px] tracking-wider uppercase text-orange-400">{ROLE_LABEL[user.role]}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
                  className="mt-4 flex items-center gap-2 text-xs text-neutral-500 hover:text-white transition-colors">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-neutral-200 px-4 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Natur" className="h-11" />
          <button data-testid="logout-btn-mobile" onClick={() => { logout(); nav("/"); }} className="text-sm text-neutral-600">Salir</button>
        </header>
        <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 bg-white border-b border-neutral-200">
          {ITEMS.filter((i) => i.roles.includes(user.role)).map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end}
                     className={({ isActive }) => `whitespace-nowrap text-xs px-3 py-1.5 rounded-full ${isActive ? "bg-orange-600 text-white" : "bg-neutral-100 text-neutral-600"}`}>
              {i.label}{i.to === "/app/mensajes" && unread > 0 ? ` (${unread > 99 ? "99+" : unread})` : ""}
            </NavLink>
          ))}
        </div>
        <main className={location.pathname.startsWith("/app/academia/drive") ? "" : "p-6 lg:p-10 max-w-6xl"}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
